/**
 * What the plugin said while the task ran.
 *
 * One component serves both surfaces that show it, the task detail screen
 * and the sandbox result panel, so the two cannot drift apart. The query is
 * a hook the bug report shares, and the plain-text rendering is one function
 * used by the copy action and appended whole to the bug report, so what the
 * operator copies and what they paste into a report are the same lines.
 *
 * The endpoint answers the empty shape for a task that has not logged, so an
 * empty answer is a state here rather than an error, and the page polls it
 * every second while the task is in flight.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { getTaskLogs } from '@optum/api-client/endpoints/tasks';
import type { TaskLog, TaskLogEntry, TaskStatus } from '@optum/api-client/types';

import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { Select } from '../../ui/Select.js';

const IN_FLIGHT = new Set<TaskStatus>(['pending', 'running', 'streaming']);
const POLL_MS = 1000;

const LEVEL_RANK: Record<string, number> = {
  DEBUG: 10,
  INFO: 20,
  WARNING: 30,
  ERROR: 40,
  CRITICAL: 50,
};

const FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All captured' },
  { value: 'DEBUG', label: 'DEBUG and above' },
  { value: 'INFO', label: 'INFO and above' },
  { value: 'WARNING', label: 'WARNING and above' },
  { value: 'ERROR', label: 'ERROR and above' },
];

const LEVEL_COLOR: Record<string, string> = {
  DEBUG: 'var(--text-muted)',
  INFO: 'var(--text-strong)',
  WARNING: 'var(--warn)',
  ERROR: 'var(--danger)',
  CRITICAL: 'var(--danger)',
};

/** The log, polled every second while the task is in flight and once after. */
export function useTaskLog(taskId: string, status: TaskStatus) {
  return useQuery({
    queryKey: qk.taskLogs(taskId),
    queryFn: () => getTaskLogs(taskId),
    refetchInterval: IN_FLIGHT.has(status) ? POLL_MS : false,
  });
}

function rank(level: string): number {
  return LEVEL_RANK[level] ?? LEVEL_RANK['INFO']!;
}

/** Entries at or above `minLevel`; every entry when none is chosen. */
export function filterEntries(entries: TaskLogEntry[], minLevel: string): TaskLogEntry[] {
  if (!minLevel) return entries;
  const floor = rank(minLevel);
  return entries.filter((entry) => rank(entry.level) >= floor);
}

/** `HH:MM:SS.mmm` from an ISO timestamp; the date is on the task, not the line. */
function clock(ts: string): string {
  const time = ts.slice(11, 23);
  return time || ts;
}

function who(entry: TaskLogEntry): string {
  return entry.logger ?? entry.source;
}

/**
 * The log as plain text: the counts, then one line per entry with the
 * exception indented beneath its line and a marker where the attempt changes.
 */
export function formatLogText(log: TaskLog): string {
  const lines: string[] = [
    `Log (${log.line_count} lines, ${log.dropped_count} dropped${log.truncated ? ', truncated' : ''})`,
  ];
  let attempt: number | null = null;
  for (const entry of log.entries) {
    if (entry.attempt !== attempt) {
      attempt = entry.attempt;
      lines.push(`[attempt ${attempt}]`);
    }
    lines.push(`${clock(entry.ts)} ${entry.level} ${who(entry)} ${entry.message}`);
    if (entry.exc) {
      lines.push(...entry.exc.split('\n').map((row) => `    ${row}`));
    }
  }
  return lines.join('\n');
}

export function LogPanel({ taskId, status }: { taskId: string; status: TaskStatus }) {
  const toast = useToast();
  const log = useTaskLog(taskId, status);
  const [minLevel, setMinLevel] = useState('');
  // Cheap enough to redo per render: the payload is capped at the collector's bound.
  const shown = filterEntries(log.data?.entries ?? [], minLevel);
  const inFlight = IN_FLIGHT.has(status);

  if (log.isPending) return <LoadingBlock />;
  if (log.isError) {
    return (
      <ErrorBlock message={log.error instanceof Error ? log.error.message : 'Failed to load'} />
    );
  }
  const data = log.data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 7,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--text-muted)',
            }}
          >
            Level
            <Select
              value={minLevel}
              onChange={(value) => setMinLevel(value)}
              aria-label="Level"
              options={[
                ...FILTER_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
              ]}
            />
          </label>
          <span
            style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
            className="mono"
          >
            {countLine(data, shown.length, inFlight)}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          icon="content_copy"
          disabled={data.line_count === 0}
          onClick={() => {
            void navigator.clipboard?.writeText(formatLogText(data));
            toast.success('Log copied');
          }}
        >
          Copy
        </Button>
      </div>

      {data.truncated && data.dropped_count > 0 && (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--warn)' }}>
          {data.dropped_count.toLocaleString()} earlier{' '}
          {data.dropped_count === 1 ? 'line was' : 'lines were'} dropped past the cap.
        </p>
      )}

      {data.line_count === 0 ? (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          {inFlight ? 'The plugin has not logged anything yet.' : 'The plugin logged nothing.'}
        </p>
      ) : shown.length === 0 ? (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          Nothing at this level or above.
        </p>
      ) : (
        <LogBlock entries={shown} />
      )}
    </div>
  );
}

function countLine(log: TaskLog, shown: number, inFlight: boolean): string {
  const total = log.line_count;
  const lines = `${total.toLocaleString()} ${total === 1 ? 'line' : 'lines'}`;
  const counted = shown === total ? lines : `${shown.toLocaleString()} of ${lines}`;
  return inFlight ? `${counted} · polling every second` : counted;
}

function LogBlock({ entries }: { entries: TaskLogEntry[] }) {
  let attempt: number | null = null;
  const rows: JSX.Element[] = [];
  for (const entry of entries) {
    if (entry.attempt !== attempt) {
      attempt = entry.attempt;
      rows.push(
        <div
          key={`attempt-${attempt}`}
          style={{
            marginTop: 5,
            borderTop: '1px solid var(--border-hairline)',
            borderStyle: 'dashed',
            borderColor: 'var(--border-hairline)',
            paddingTop: 5,
            fontSize: 'var(--text-meta-lg)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-muted)',
          }}
        >
          attempt {attempt}
        </div>,
      );
    }
    rows.push(<LogLine key={`${entry.attempt}-${entry.seq}`} entry={entry} />);
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        overflowX: 'auto',
        borderRadius: 'var(--radius-input)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-fill)',
        padding: 7,
        fontSize: 'var(--text-meta-lg)',
        lineHeight: 1.625,
      }}
      className="mono"
    >
      {rows}
    </div>
  );
}

function LogLine({ entry }: { entry: TaskLogEntry }) {
  const levelColor = LEVEL_COLOR[entry.level] ?? 'var(--text-strong)';
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '88px 60px minmax(0,160px) minmax(0,1fr)',
        alignItems: 'baseline',
        gap: 7,
      }}
    >
      <span style={{ color: 'var(--text-muted)' }}>{clock(entry.ts)}</span>
      <span style={{ fontWeight: 'var(--weight-semibold)', color: levelColor }}>{entry.level}</span>
      <span
        title={who(entry)}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'var(--text-muted)',
        }}
      >
        {entry.logger ?? <SourceTag source={entry.source} />}
      </span>
      <span
        style={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          color:
            entry.level === 'ERROR' || entry.level === 'CRITICAL'
              ? 'var(--danger)'
              : 'var(--text-strong)',
        }}
      >
        {entry.message}
      </span>
      {entry.exc && (
        <pre
          style={{
            gridColumnStart: 4,
            whiteSpace: 'pre-wrap',
            borderLeft: '2px solid var(--border-hairline)',
            borderColor: 'var(--danger-tint)',
            paddingLeft: 11,
            fontSize: 'var(--text-meta-lg)',
            color: 'var(--danger)',
          }}
        >
          {entry.exc}
        </pre>
      )}
    </div>
  );
}

function SourceTag({ source }: { source: string }) {
  return (
    <span
      style={{
        borderRadius: 'var(--radius-chip)',
        border: '1px solid var(--border-hairline)',
        paddingLeft: 5,
        paddingRight: 5,
        fontSize: 'var(--text-meta-lg)',
        color: 'var(--text-muted)',
      }}
    >
      {source}
    </span>
  );
}
